import React from "react";
import { NavigateFunction } from "react-router-dom";
import InvoicesIcon from "../../../../../images/common/invoice-blue.svg";
import InvoiceIcon from "../../../../../images/common/invoice-blue.svg";
import UpsertInvoicePopUp from "./UpsertInvoicePopUp";
import {
  ContactReferenceModel,
  CountryWithStateModel,
  RelatedInvoicesGridModel,
  UpsertInvoiceModel,
} from "../models";
import { GridNames, InvoiceStatus, NewPaymentRelatedSource } from "../enums";
import {
  AuthService,
  CustomButton,
  ITableHeaders,
  TableWithHeaderButtons,
  UtilityMethods,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import { InvoiceUtil } from "../utils";
import PlaceholderSmallerGrid from "./PlaceholderSmallerGrid";
import TableGrid from "./TableGrid";
import { AccountSettingsStore, RolesAndPermissionStore } from "../store";  

type IProps = {
  navigate: NavigateFunction;
  invoices: RelatedInvoicesGridModel[];
  contact?: ContactReferenceModel;
  onUpdate?: () => void;
  source: keyof typeof NewPaymentRelatedSource;
  sourceId: string; //id of contact, subscription, invoice, checkout, purchaselinks
  rolesAndPermissionStore: RolesAndPermissionStore;
  disableNewButton?: boolean;
  countriesWithState: CountryWithStateModel[];
};
const RelatedInvoicesGrid: React.FC<IProps> = ({
  invoices,
  navigate,
  contact,
  onUpdate,
  source,
  sourceId,
  rolesAndPermissionStore,
  disableNewButton,
  countriesWithState,
}) => {
  const [showInvoicePopUp, setShowInvoicePopUp] = React.useState(false);

  const headers = () => {
    const columns: ITableHeaders<RelatedInvoicesGridModel>[] = [
      {
        dataKey: "status",
        title: "Status",
        onCellRender: (data: RelatedInvoicesGridModel) => {
          const colors = InvoiceUtil.getColorsByStatus(data.status);
          return (
            <CustomButton color={colors.color} bgColor={colors.bgColor}>
              {InvoiceStatus[data.status]}
            </CustomButton>
          );
        },
      },
      {
        dataKey: "dueDate",
        title: "Date",
        onCellRender: (data: RelatedInvoicesGridModel) => {
          return UtilityMethods.formatDateInString(data.dueDate);
        },
      },
      {
        dataKey: "netAmount",
        title: "Amount",

        onCellRender: (data: RelatedInvoicesGridModel) => {
          return "$" + AccountSettingsStore.formatAmount(data.netAmount, true);
        },
      },
      {
        dataKey: "description",
        title: "Description",
        onCellRender: (data: RelatedInvoicesGridModel) => {
          return data.description;
        },
      },
      {
        dataKey: "paidAmount",
        title: "Paid",
        onCellRender: (data: RelatedInvoicesGridModel) => {
          return "$" + AccountSettingsStore.formatAmount(data.paidAmount, true);
        },
      },
      {
        dataKey: "pastDueDays",
        title: "Past Due",
        onCellRender: (data: RelatedInvoicesGridModel) => {
          return data.pastDueDays > 0 ? data.pastDueDays + " days" : "";
        },
      },
    ];
    return columns;
  };

  function togglePopUp(show: boolean) {
    setShowInvoicePopUp(show);
  }

  function onclose(refresh: boolean) {
    togglePopUp(false);
    if (refresh) {
      onUpdate && onUpdate();
    }
  }

  if (invoices.length == 0)
    return (
      <>
        {showInvoicePopUp && (
          <UpsertInvoicePopUp
            invoiceData={
              new UpsertInvoiceModel({
                contact: contact,
                source: source,
                sourceId: sourceId,
                time: UtilityMethods.getHHMMFromDate(new Date()),
                amPm: UtilityMethods.getAMPMFromDate(new Date()),
              })
            }
            countriesWithState ={countriesWithState}
            onUpdate={() => togglePopUp(false)}
            onClose={() => togglePopUp(false)}
            contact={contact}
          />
        )}
        <PlaceholderSmallerGrid
          title="No Invoices"
          disableButton={disableNewButton === true}
          icon={InvoiceIcon}
          buttonCaption="New"
          buttonClick={() => {
            if (!rolesAndPermissionStore.hasManagePermission("Create")) return;
            togglePopUp(true);
          }}
        />
      </>
    );

  return (
    <>
      {showInvoicePopUp && (
        <UpsertInvoicePopUp
          invoiceData={
            new UpsertInvoiceModel({
              contact: contact,
              source: source,
              sourceId: sourceId,
              time: UtilityMethods.getHHMMFromDate(new Date()),
              amPm: UtilityMethods.getAMPMFromDate(new Date()),
            })
          }
          onUpdate={() => togglePopUp(false)}
          onClose={(refresh: boolean) => onclose(refresh)}
          contact={contact}
          countriesWithState ={countriesWithState}
        />
      )}
      <TableGrid
        onRowClick={(data: RelatedInvoicesGridModel) =>
          !AuthService.isMindMeContactRoute &&
          navigate(`/app/invoices/detail/${data.id}`)
        }
        wrapperClassName="table-with-header-btns"
        tableHeader={
          <TableWithHeaderButtons
            isShowHeaderButton={true}
            totalItems={invoices.length}
            icon={InvoicesIcon}
            title="Invoices"
            onNewButtonClick={() => {
              if (!rolesAndPermissionStore.hasManagePermission("Create"))
                return;
              togglePopUp(true);
            }} 
          />
        }
        cellSpacing={20}
        headers={headers()}
        data={invoices}
        totalItemCount={invoices.length}
        name={GridNames.Invoice}
        countriesWithState={countriesWithState}
      />
    </>
  );
};

export default withRouter(RelatedInvoicesGrid);
